import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import { ICaptableDataModel } from '../../data-models/captable2.data-model';
import { dateToPlainDateString } from '../../util/date-utilts';
import {
  getCapTablesForCompany,
  getCaptableById,
  ICaptableVersionGroup,
} from '../queries/MaggieCapTableQueries';

type CapTablesValue = ICaptableDataModel[] | null | Promise<ICaptableDataModel[] | null>;
type CaptableVersionsValue = ICaptableVersionGroup[] | null | Promise<ICaptableVersionGroup[] | null>;

/** Per-company "as of" date for the cap-table view. Defaults to today. */
export const captableAsOfDateStateJ = atomFamily((_companyId: number) =>
  atom<string>(dateToPlainDateString(new Date()))
);

/**
 * Default fetches all cap tables for the company. Returns `null` (without
 * fetching) when `companyId <= 0` — `useFetchCaptablesLegacy` and
 * `useFetchCaptablesHistorical` both subscribe via `selectedCompanyIdProfileAtom`,
 * which defaults to `-1` before a profile is selected.
 *
 * Consumers using `useAtomValue` will suspend on first read; callers needing
 * a non-suspending peek should use {@link captablesByCompanyStateLoadableJ}.
 */
export const captablesByCompanyStateJ = atomFamily((companyId: number) =>
  atomWithDefault<CapTablesValue>(async () => {
    if (companyId <= 0) {
      return null;
    }
    return getCapTablesForCompany(companyId);
  })
);

export const captablesByCompanyStateLoadableJ = atomFamily((companyId: number) =>
  loadable(captablesByCompanyStateJ(companyId))
);

/**
 * Per-source, effective-dated version timelines for the company (FD-3823/FD-3733). Drives the source +
 * version picklists. Fetched and populated by `useFetchCaptableVersions` (and refreshed by
 * `useInvalidateCaptables`); defaults to `null` until then.
 */
export const captableVersionsByCompanyStateJ = atomFamily((_companyId: number) =>
  atomWithDefault<CaptableVersionsValue>(() => null)
);

/**
 * A single cap table fetched by id. Used to resolve a selected version that is not the current one and
 * therefore isn't in {@link captablesByCompanyStateJ}. Returns `null` for a non-positive id.
 */
export const captableByIdStateJ = atomFamily((id: number) =>
  atom(async (): Promise<ICaptableDataModel | null> => (id > 0 ? getCaptableById(id) : null))
);

/**
 * Primary cap table for the company, or null if none flagged primary.
 * Derived from {@link captablesByCompanyStateJ}.
 */
export const primaryCaptableStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<ICaptableDataModel | null> => {
    const captables = await get(captablesByCompanyStateJ(companyId));
    if (!captables) {
      return null;
    }
    return captables.find((c) => c.primary) ?? null;
  })
);

/**
 * Primary cap table, or the first one if no primary is set, or null.
 */
export const primaryOrDefaultCaptableStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<ICaptableDataModel | null> => {
    const captables = await get(captablesByCompanyStateJ(companyId));
    const primary = await get(primaryCaptableStateJ(companyId));
    return primary ?? captables?.at(0) ?? null;
  })
);

/**
 * Map of cap-table id → cap table for the company.
 * Includes only current cap tables; stale ones can only be fetched by id.
 */
export const companyCaptablesByIdStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<Map<number, ICaptableDataModel>> => {
    const captables = await get(captablesByCompanyStateJ(companyId));
    if (!captables) {
      return new Map();
    }
    return new Map(captables.map((c) => [c.id, c]));
  })
);

/**
 * Currently-selected cap-table id for the company. Defaults to the primary
 * (or first) cap table's id.
 *
 * Implemented as a writable atomFamily backed by `atomWithDefault` so callers
 * can override the id explicitly (e.g. after invalidating cap tables) and
 * reset it back to the derived default by writing `RESET`.
 */
export const selectedCaptableIdStateJ = atomFamily((companyId: number) =>
  atomWithDefault<number | null | Promise<number | null>>(async (get) => {
    const primaryOrDefault = await get(primaryOrDefaultCaptableStateJ(companyId));
    return primaryOrDefault?.id ?? null;
  })
);

/**
 * Currently-selected cap table for the company.
 */
export const selectedCaptableStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<ICaptableDataModel | null> => {
    const selectedId = await get(selectedCaptableIdStateJ(companyId));
    if (selectedId == null) {
      return null;
    }
    const byId = await get(companyCaptablesByIdStateJ(companyId));
    const fromList = byId.get(selectedId);
    if (fromList) {
      return fromList;
    }
    // A selected historical version isn't in the current list; fetch it by id. Never throw — a
    // deleted/forbidden id resolves to null so readers degrade gracefully instead of crashing.
    try {
      const byIdFetch = await get(captableByIdStateJ(selectedId));
      // `captableByIdStateJ` is unfiltered and cached for the session, so an id belonging to another
      // company would otherwise resolve non-null forever and suppress the repair path.
      return byIdFetch?.companyId === companyId ? byIdFetch : null;
    } catch (err) {
      console.error(err);
      return null;
    }
  })
);
